import type { QueryClient } from "@tanstack/react-query";
import { useChatStore } from "@/features/chat/chatStore";
import { useDraftChats } from "@/features/chat/draftChatStore";
import { useFileDrafts } from "@/features/files/fileDraftStore";
import { useChatComposers } from "@/features/chat/chatComposerStore";
import { useHandoffDrafts } from "@/features/chat/handoffDraftStore";
import { useUnreadChats } from "@/features/chat/unreadStore";
import { useAssistantDrawer } from "@/features/assistant/assistantStore";
import { useTerminalSessionStore } from "@/features/worktree/terminalSessionStore";
import { useArchiveToast } from "./archiveToastStore";
import { useCommandPalette } from "./paletteStore";
import { usePendingSends } from "@/features/chat/pendingSends";
import { clearPersistedData } from "./persistence";

/** Called when the organization itself (or the session) is gone, after
 * protected views unmount and before reloading the page. Clearing the query
 * cache also cancels old reads, including clients that ignore AbortSignal. A
 * reload fences mutation callbacks and component-local file/direct-terminal
 * buffers that cannot be safely reset through the shared stores alone. The
 * in-memory composers are cleared too; what is unsent was written down first
 * (`prepareForReload`) and is kept for an expired session, erased for an
 * organization the member left. An ordinary `access.changed` never comes here:
 * it reconciles in place. Personal preferences (theme, shortcuts) are kept. */
export function clearAccessScopedState(cache: QueryClient): Promise<void> {
  cache.clear();
  // What this browser kept on disk was read under the old access. The caller
  // reloads next; it awaits this (bounded), because an IndexedDB delete still
  // in flight at unload is aborted and the old records would survive.
  const cleared = clearPersistedData();
  useChatStore.getState().resetAll();
  usePendingSends.getState().reset();
  for (const id of Object.keys(useDraftChats.getState().drafts)) useDraftChats.getState().close(id);
  useFileDrafts.getState().reset();
  useChatComposers.getState().reset();
  useHandoffDrafts.getState().reset();
  useUnreadChats.getState().reset();
  useAssistantDrawer.getState().activateContext(null);
  useTerminalSessionStore.getState().reset();
  useArchiveToast.getState().dismiss();
  useCommandPalette.setState({ open: false, contextCommands: [] });
  return cleared;
}

/** How long a reload waits for the disk copy to be deleted. */
export const ACCESS_RESET_CLEAR_BOUND_MS = 1_500;

/** The delete, or the bound, whichever comes first. */
export function boundedClear(cleared: Promise<void>, boundMs = ACCESS_RESET_CLEAR_BOUND_MS): Promise<void> {
  return Promise.race([cleared.catch(() => undefined), new Promise<void>((resolve) => setTimeout(resolve, boundMs))]);
}
